import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);

    // Handle 401 unauthorized - redirect to login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on login/register page
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/register')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Lead APIs
export const leadApi = {
  getAll: (params) => api.get('/leads', { params }),
  getById: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convertToClient: (id, data) => api.post(`/leads/${id}/convert`, data),
  importLeads: (data) => api.post('/leads/import', data),
  exportLeads: (params) => api.get('/leads/export', { params }),
  getPipeline: () => api.get('/leads/pipeline'),
  getTodayTasks: () => api.get('/leads/today-tasks'),
  incrementWarmup: (id, type) => api.post(`/leads/${id}/warmup`, { type }),
  getActivities: (id, params) => api.get(`/leads/${id}/activities`, { params }),
  // Follow-up management
  addFollowup: (id, data) => api.post(`/leads/${id}/followups`, data),
  completeFollowup: (id, followupId) => api.put(`/leads/${id}/followups/${followupId}/complete`),
  deleteFollowup: (id, followupId) => api.delete(`/leads/${id}/followups/${followupId}`),
  bulkScheduleFollowup: (data) => api.post('/leads/bulk-followup', data),
  getCalendarFollowups: (params) => api.get('/leads/calendar', { params }),
};

// Activity APIs
export const activityApi = {
  getRecent: (limit) => api.get('/activities/recent', { params: { limit } }),
  create: (data) => api.post('/activities', data),
  delete: (id) => api.delete(`/activities/${id}`),
  getStats: (days) => api.get('/activities/stats', { params: { days } }),
};

// Client APIs
export const clientApi = {
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  cancel: (id, reason) => api.post(`/clients/${id}/cancel`, { reason }),
  pause: (id) => api.post(`/clients/${id}/pause`),
  reactivate: (id) => api.post(`/clients/${id}/reactivate`),
  getSupportLogs: (id) => api.get(`/clients/${id}/support`),
  getInvoices: (id) => api.get(`/clients/${id}/invoices`),
  getPayments: (id) => api.get(`/clients/${id}/payments`),
  getStats: () => api.get('/clients/stats'),
};

// Invoice APIs
export const invoiceApi = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: (id) => api.post(`/invoices/${id}/send`),
  markPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data),
  generateMonthly: () => api.post('/invoices/generate-monthly'),
};

// Billing APIs
export const billingApi = {
  getSummary: () => api.get('/billing/summary'),
  getOverdue: () => api.get('/billing/overdue'),
  createPayment: (data) => api.post('/billing/payments', data),
};

// Support APIs
export const supportApi = {
  create: (data) => api.post('/support', data),
  update: (id, data) => api.put(`/support/${id}`, data),
};

// Analytics APIs
export const analyticsApi = {
  getDashboard: () => api.get('/analytics/dashboard'),
  getMRR: (months) => api.get('/analytics/mrr', { params: { months } }),
  getPipeline: (params) => api.get('/analytics/pipeline', { params }),
  getConversionByNiche: () => api.get('/analytics/conversion-by-niche'),
  getChurn: (months) => api.get('/analytics/churn', { params: { months } }),
  getRevenue: (months) => api.get('/analytics/revenue', { params: { months } }),
  getClientGrowth: (months) => api.get('/analytics/client-growth', { params: { months } }),
  createSnapshot: () => api.post('/analytics/snapshot'),
};

// Settings APIs
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// DM Template APIs
export const dmTemplateApi = {
  getAll: (params) => api.get('/dm-templates', { params }),
  getById: (id) => api.get(`/dm-templates/${id}`),
  create: (data) => api.post('/dm-templates', data),
  update: (id, data) => api.put(`/dm-templates/${id}`, data),
  delete: (id) => api.delete(`/dm-templates/${id}`),
  preview: (id, data) => api.post(`/dm-templates/${id}/preview`, data),
};

// API Key APIs
export const apiKeyApi = {
  getAll: (params) => api.get('/api-keys', { params }),
  getById: (id) => api.get(`/api-keys/${id}`),
  create: (data) => api.post('/api-keys', data),
  update: (id, data) => api.put(`/api-keys/${id}`, data),
  delete: (id) => api.delete(`/api-keys/${id}`),
  regenerate: (id) => api.post(`/api-keys/${id}/regenerate`),
};

// Contact APIs
export const contactApi = {
  getAll: (params) => api.get('/contacts', { params }),
  getById: (id) => api.get(`/contacts/${id}`),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  getStats: () => api.get('/contacts/stats'),
  getApiKeys: () => api.get('/contacts/api-keys'),
};

// Tracking / Page Visit APIs
export const trackingApi = {
  getAll: (params) => api.get('/track/visits', { params }),
  getLeadVisits: (leadId, params) => api.get(`/track/visits/lead/${leadId}`, { params }),
  getByDomain: (domain, params) => api.get(`/track/visits/domain/${encodeURIComponent(domain)}`, { params }),
  getAnalytics: (params) => api.get('/track/analytics', { params }),
  linkToLead: (visitId, leadId) => api.patch(`/track/visits/${visitId}/link`, { leadId }),
  delete: (visitId) => api.delete(`/track/visits/${visitId}`),
  getActiveLeads: (params) => api.get('/track/active-leads', { params }),
};

export default api;
